import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth-context";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const isInvite = params.get("invite") === "1";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(isInvite ? "Password set - signing you in." : "Password updated - signing you in.");
      const res = await login(data.email, password);
      if (res.mfaRequired) navigate("/login", { state: { mfaToken: res.mfaToken, rememberAllowed: res.rememberAllowed } });
      else navigate(res.user?.role === "admin" ? "/admin/patients" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full rounded-sm border border-stone-300 bg-white px-4 py-3 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-bronze-dark/40 focus:border-bronze-dark";

  if (!token) {
    return (
      <div data-testid="reset-page-no-token" className="container-ccc py-16 max-w-md">
        <h1 className="font-serif text-3xl">Invalid link</h1>
        <p className="mt-4 text-ink-muted">This password link is missing its security token. Please request a new one.</p>
        <Link to="/forgot-password" className="btn-primary mt-6 inline-block">Request a new link</Link>
      </div>
    );
  }

  return (
    <div data-testid="reset-page" className="container-ccc py-16 max-w-md">
      <div className="eyebrow">{isInvite ? "Welcome to the patient portal" : "Password reset"}</div>
      <h1 className="font-serif text-4xl mt-3">
        {isInvite ? "Set your password." : "Choose a new password."}
      </h1>
      <p className="mt-3 text-ink-muted text-sm">
        {isInvite
          ? "Your patient portal account is ready. Choose a password to start using it."
          : "Choose a new password to finish resetting your account."}
      </p>
      <form onSubmit={submit} data-testid="reset-form" className="mt-10 space-y-5">
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">New password</label>
          <input required type="password" minLength={8} data-testid="reset-password-input"
            className={input} value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">Confirm password</label>
          <input required type="password" minLength={8} data-testid="reset-confirm-input"
            className={input} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        {error && <div className="text-sm text-destructive" data-testid="reset-error">{error}</div>}
        <button disabled={loading} type="submit" data-testid="reset-submit-button" className="btn-primary w-full">
          {loading ? "Saving…" : isInvite ? "Set password & continue" : "Update password"}
        </button>
      </form>
    </div>
  );
}
